import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus, InteractionType, EventType } from '@azure/msal-browser';
import { loginRequest } from '../auth/msalConfig';
import { SYSTEM_ROLES } from '../auth/permissions';
import {
  exchangeCiamToken,
  forceReLogin,
  classifyAuthFailure,
  REAUTH_REQUIRED,
  NOT_AUTHORIZED,
  TRANSIENT,
  TIFOXR_TOKEN_KEY,
  TIFOXR_TOKEN_EXPIRY_KEY,
  TIFOXR_SYSTEM_ROLE_KEY,
  TIFOXR_ENTITY_ID_KEY,
  TIFOXR_USER_ID_KEY,
  TIFOXR_USER_NAME_KEY,
  TIFOXR_USER_EMAIL_KEY,
  TIFOXR_SPACES_KEY,
  TIFOXR_ACTIVE_SPACE_KEY,
} from '../api/apiConfig';
import { signalRService } from '../services/signalRService';
import { SIGNALR_EVENTS } from '../constants/signalREvents';

const AuthContext = createContext(null);

const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

const MAX_PROACTIVE_REFRESH_RETRIES = 2;

// attemptExchange only ever runs once the cached CMS token is already confirmed dead, so a
// TRANSIENT failure there (e.g. MSAL's block_iframe_reload/timed_out) isn't a network blip to
// wait out -- it means the underlying Microsoft session itself is gone. One short retry rules out
// a genuine one-off glitch before giving up on silent auth.
const ATTEMPT_EXCHANGE_RETRY_DELAY_MS = 1500;

function safeParseStorage(key, fallback) {
  const raw = localStorage.getItem(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    localStorage.removeItem(key);
    return fallback;
  }
}

function scheduleProactiveRefresh(account, timerRef, retryCount = 0) {
  const expiry = localStorage.getItem(TIFOXR_TOKEN_EXPIRY_KEY);
  if (!expiry) return;
  const delay = new Date(expiry).getTime() - Date.now() - REFRESH_BEFORE_EXPIRY_MS;

  const attemptRefresh = async () => {
    try {
      await exchangeCiamToken(account);
      scheduleProactiveRefresh(account, timerRef);
    } catch (err) {
      const failure = classifyAuthFailure(err);

      if (failure.kind === REAUTH_REQUIRED) {
        // Retrying cannot help -- the Microsoft credential itself is unusable.
        forceReLogin(failure.message);
        return;
      }

      if (failure.kind === NOT_AUTHORIZED) {
        // Re-auth can't create a missing CMS account — notify only, don't wipe MSAL cache or navigate away.
        window.dispatchEvent(new CustomEvent('api-error', { detail: { message: failure.message } }));
        return;
      }

      if (retryCount + 1 >= MAX_PROACTIVE_REFRESH_RETRIES) {
        // Within the pre-expiry grace window; JWT still valid — the 401 interceptor is the real safety net.
        window.dispatchEvent(new CustomEvent('api-error', { detail: { message: failure.message } }));
        return;
      }
      timerRef.current = setTimeout(
        () => scheduleProactiveRefresh(account, timerRef, retryCount + 1),
        60_000,
      );
    }
  };

  if (delay <= 0) {
    attemptRefresh();
    return;
  }
  clearTimeout(timerRef.current);
  timerRef.current = setTimeout(attemptRefresh, delay);
}

export function AuthProvider({ children }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const isLoading       = inProgress !== InteractionStatus.None;
  const refreshTimerRef = useRef(null);
  const attemptExchangeRetryTimerRef = useRef(null);

  const [userId, setUserId] = React.useState(
    () => localStorage.getItem(TIFOXR_USER_ID_KEY) || null,
  );
  const [systemRole, setSystemRole] = React.useState(
    () => localStorage.getItem(TIFOXR_SYSTEM_ROLE_KEY) || null,
  );
  const [activeSpace, setActiveSpaceState] = React.useState(
    () => safeParseStorage(TIFOXR_ACTIVE_SPACE_KEY, null),
  );
  const [signInError, setSignInError] = React.useState(null);

  const [isExchanging, setIsExchanging] = React.useState(false);

  const setActiveSpace = (space) => {
    if (space) {
      localStorage.setItem(TIFOXR_ACTIVE_SPACE_KEY, JSON.stringify(space));
    } else {
      localStorage.removeItem(TIFOXR_ACTIVE_SPACE_KEY);
    }
    setActiveSpaceState(space);
  };

  const user = accounts[0]
    ? {
        id:         userId,
        name:       localStorage.getItem(TIFOXR_USER_NAME_KEY) || accounts[0].name || accounts[0].username,
        email:      localStorage.getItem(TIFOXR_USER_EMAIL_KEY) || accounts[0].username,
        systemRole,
        entityId:   localStorage.getItem(TIFOXR_ENTITY_ID_KEY)
                      ? parseInt(localStorage.getItem(TIFOXR_ENTITY_ID_KEY), 10)
                      : null,
        spaces:     safeParseStorage(TIFOXR_SPACES_KEY, []),
      }
    : null;

  const login  = () => {
    if (inProgress === InteractionStatus.None) {
      // prompt: 'select_account' -- shown only here (not on the silent acquireTokenSilent calls
      // that reuse loginRequest), so a returning user always picks which cached MS account to use
      // instead of silently resuming whichever session Microsoft still has active.
      instance.loginRedirect({ ...loginRequest, prompt: 'select_account' });
    }
  };

  // No MS sign-out redirect — SSO session persists, so next sign-in may skip the password prompt.
  const logout = async () => {
    clearTimeout(refreshTimerRef.current);
    setUserId(null);
    setSystemRole(null);
    await forceReLogin();
  };

  // Ensures activeSpace is set from localStorage after a token exchange or on page
  // refresh. Login.jsx only runs after a fresh Azure AD redirect, so page-refresh
  // and silent re-auth flows need this to restore (or auto-select) the active space.
  const syncActiveSpace = (resolvedRole) => {
    const spaces          = safeParseStorage(TIFOXR_SPACES_KEY, []);
    const sysRole         = resolvedRole ?? localStorage.getItem(TIFOXR_SYSTEM_ROLE_KEY) ?? null;
    const isSysUser       = sysRole === SYSTEM_ROLES.GLOBAL_ADMIN || sysRole === SYSTEM_ROLES.ENTITY_ADMIN;
    const isGmsAccountant = sysRole === SYSTEM_ROLES.GMS_ACCOUNTANT;

    // A stored active space only counts as already-set if it still matches the CURRENT role --
    // otherwise a leftover '__finance__'/'__admin__' sentinel (or a real space) from a different
    // role/user in this same browser would silently block the correct one from being set below.
    const stored = safeParseStorage(TIFOXR_ACTIVE_SPACE_KEY, null);
    if (stored) {
      const storedMatchesRole = isGmsAccountant
        ? stored.spaceId === '__finance__'
        : isSysUser
        ? stored.spaceId === '__admin__'
        : spaces.some((s) => s.spaceId === stored.spaceId);
      if (storedMatchesRole) {
        // The stored copy keeps the roles it was saved with, so a role change made since then would
        // keep driving the menu and route guards. The spaces list is rewritten on every sign-in.
        const fresh = !isSysUser && !isGmsAccountant ? spaces.find((s) => s.spaceId === stored.spaceId) : null;
        if (fresh && JSON.stringify(fresh.roles ?? []) !== JSON.stringify(stored.roles ?? [])) setActiveSpace(fresh);
        return;
      }
    }

    if (isGmsAccountant) {
      setActiveSpace({ spaceId: '__finance__', roles: [], _isFinance: true });
    } else if (!isSysUser && spaces.length === 1) {
      setActiveSpace(spaces[0]);
    } else if (isSysUser && spaces.length === 0) {
      setActiveSpace({ spaceId: '__admin__', roles: [], _isAdmin: true });
    }
  };

  const attemptExchange = async (account, isRetry = false) => {
    setIsExchanging(true);
    try {
      await exchangeCiamToken(account);
      const newRole      = localStorage.getItem(TIFOXR_SYSTEM_ROLE_KEY) || null;
      const newIsSysUser = newRole === SYSTEM_ROLES.GLOBAL_ADMIN || newRole === SYSTEM_ROLES.ENTITY_ADMIN;

      if (newIsSysUser) setActiveSpace(null);

      setUserId(localStorage.getItem(TIFOXR_USER_ID_KEY) || null);
      setSystemRole(newRole);
      syncActiveSpace(newRole);
      setSignInError(null);
      setIsExchanging(false);
      scheduleProactiveRefresh(account, refreshTimerRef);
    } catch (err) {
      console.error('CMS token exchange failed:', err);
      const failure = classifyAuthFailure(err);

      // REAUTH_REQUIRED: MSAL confirmed the credential is unusable, so reset now instead of
      // waiting for a manual click.
      if (failure.kind === REAUTH_REQUIRED) {
        setSignInError(failure);
        setIsExchanging(false);
        forceReLogin(failure.message);
        return;
      }

      if (failure.kind === TRANSIENT) {
        if (!isRetry) {
          // One short silent retry in case this was a genuine one-off glitch (e.g. a slow
          // network). Stay in the "signing in" state -- no user-visible error yet.
          attemptExchangeRetryTimerRef.current = setTimeout(
            () => attemptExchange(account, true),
            ATTEMPT_EXCHANGE_RETRY_DELAY_MS,
          );
          return;
        }
        // Retried once and it failed again the same way (e.g. MSAL's block_iframe_reload/
        // timed_out): the Microsoft session is gone, not flaky. Waiting on a "Try Again" click
        // would just repeat the identical doomed silent call, so go straight to an interactive
        // re-login instead of looping.
        setIsExchanging(false);
        forceReLogin(failure.message);
        return;
      }

      // NOT_AUTHORIZED: re-auth can't create a missing CMS account -- show the message, don't
      // wipe MSAL cache or navigate away. Only classification that leaves a user-clickable
      // "Try Again" (retrySignIn re-runs the exchange, which is harmless and may now succeed).
      setSignInError(failure);
      setIsExchanging(false);
    }
  };

  const retrySignIn = () => {
    if (!signInError) return;
    const failure = signInError;
    setSignInError(null);
    if (failure.kind === REAUTH_REQUIRED) {
      forceReLogin(failure.message);
      return;
    }
    // NOT_AUTHORIZED/TRANSIENT: re-running the exchange is harmless and may now succeed.
    if (accounts[0]) {
      attemptExchange(accounts[0]);
    } else {
      login();
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !accounts[0] || isLoading) return;

    const existingToken = localStorage.getItem(TIFOXR_TOKEN_KEY);
    const expiry        = localStorage.getItem(TIFOXR_TOKEN_EXPIRY_KEY);
    // Also treat old-format sessions (no tifoxr_spaces) as invalid so doExchange runs.
    const hasNewFormat  = localStorage.getItem(TIFOXR_SPACES_KEY) !== null;
    const cachedRole    = localStorage.getItem(TIFOXR_SYSTEM_ROLE_KEY) || '';
    // entity_admin must have entity_id in the token — force re-exchange if missing.
    const staleCache    = cachedRole === SYSTEM_ROLES.ENTITY_ADMIN && !localStorage.getItem(TIFOXR_ENTITY_ID_KEY);
    const isValid       = existingToken && expiry && new Date(expiry) > new Date() && hasNewFormat && !staleCache;

    if (isValid) {
      syncActiveSpace(null);
      scheduleProactiveRefresh(accounts[0], refreshTimerRef);
      return;
    }

    setSignInError(null);
    attemptExchange(accounts[0]);
  }, [isAuthenticated, accounts[0]?.homeAccountId, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      clearTimeout(refreshTimerRef.current);
      clearTimeout(attemptExchangeRetryTimerRef.current);
    };
  }, []);

  // Always mounted; cancels pending timers to avoid racing forceReLogin's async clearCache().
  useEffect(() => {
    const handleForceRelogin = () => {
      clearTimeout(refreshTimerRef.current);
      clearTimeout(attemptExchangeRetryTimerRef.current);
    };
    window.addEventListener('tifoxr-force-relogin', handleForceRelogin);
    return () => window.removeEventListener('tifoxr-force-relogin', handleForceRelogin);
  }, []);

  // When the backend updates this user's role, it sends ForceLogout via SignalR.
  // Clear session immediately so their next page load re-exchanges with fresh roles.
  useEffect(() => {
    if (!isAuthenticated) return;
    // Cancel the refresh timer first, or it could re-write keys forceReLogin() is mid-clearing.
    const handleForceLogout = () => {
      clearTimeout(refreshTimerRef.current);
      forceReLogin();
    };
    signalRService.on(SIGNALR_EVENTS.FORCE_LOGOUT, handleForceLogout);
    return () => signalRService.off(SIGNALR_EVENTS.FORCE_LOGOUT, handleForceLogout);
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const callbackId = instance.addEventCallback((message) => {
      if (
        message.eventType === EventType.ACQUIRE_TOKEN_FAILURE &&
        message.interactionType === InteractionType.Redirect &&
        message.error
      ) {
        console.error('Microsoft sign-in redirect failed:', message.error);
        const failureMessage = 'Signing in with Microsoft didn’t complete. Please try again.';
        // Matches other REAUTH_REQUIRED paths — a failed redirect can leave MSAL's cache half-completed.
        setSignInError({ kind: REAUTH_REQUIRED, message: failureMessage });
        forceReLogin(failureMessage);
      }
    });
    return () => {
      if (callbackId) instance.removeEventCallback(callbackId);
    };
  }, [instance]);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, login, logout, activeSpace, setActiveSpace, signInError, retrySignIn, isExchanging }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
